/**
 * ErrorBoundary Component
 * WHY: Catches unhandled React errors and shows a fallback UI with error logging.
 * Also installs global window.onerror and unhandledrejection handlers for APM.
 */

import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };

    // Install global error handlers for uncaught errors and unhandled promise rejections
    if (typeof window !== "undefined") {
      window.addEventListener("error", (event) => {
        this.logGlobalError(event.error || new Error(event.message), event.filename || window.location.href);
      });
      window.addEventListener("unhandledrejection", (event) => {
        const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        this.logGlobalError(error, "unhandled-promise");
      });
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    this.logError(error, errorInfo);
  }

  async logError(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      await supabase.from("error_logs").insert({
        user_id: session?.user?.id ?? null,
        error_message: error.message,
        error_stack: error.stack ?? null,
        url: window.location.href,
        user_agent: navigator.userAgent,
        additional_data: {
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
        },
      });
    } catch (err) {
      console.error("Failed to log error:", err);
    }
  }

  async logGlobalError(error: Error, source: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await supabase.from("error_logs").insert({
        user_id: session?.user?.id ?? null,
        error_message: error.message,
        error_stack: error.stack ?? null,
        url: source,
        user_agent: navigator.userAgent,
        additional_data: {
          type: "global_handler",
          timestamp: new Date().toISOString(),
        },
      });
    } catch {
      // silently fail
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen bg-auth-background flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <AlertTriangle className="h-12 w-12 text-yellow-400 mx-auto" />
            <h1 className="text-xl font-semibold text-auth-foreground">
              Something went wrong
            </h1>
            <p className="text-auth-muted text-sm">
              We're sorry, but something unexpected happened. Our team has been
              notified.
            </p>
            {this.state.error && (
              <pre className="text-left text-xs bg-auth-card border border-auth-border rounded-lg p-4 overflow-auto max-h-60 text-destructive">
                {this.state.error.message}
                {"\n\n"}
                {this.state.error.stack}
              </pre>
            )}
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                className="border-auth-border text-auth-foreground"
                onClick={() => (window.location.href = "/dashboard")}
              >
                Go to Dashboard
              </Button>
              <Button
                className="bg-auth-accent hover:bg-auth-accent/90 text-white"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
